import React, { useState, useEffect, useRef, Suspense, lazy } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHistory, faQuestionCircle, faUserSecret, faCircleInfo } from '@fortawesome/free-solid-svg-icons'

// Data Modules, CONSTANTS, Helpers
import { DATE_FIELDS, MODULE_FIELD_TYPES } from '../../constants'
import { useRequest } from '../../hooks/UseRequest'
import { hasUserPermission } from '../../utils/permissionHelper'

// REDUX
import { selectAuth } from "../../redux/authSlice"
import { setSidebarIsActive } from "../../redux/layoutSlice"
import { setModal, selectModal } from "../../redux/modalSlice"
import { selectModules, fetchModules } from '../../redux/moduleSlice'
import { selectDropdownOptions, selectDropdowns, fetchDropdowns } from '../../redux/dropdownSlice'
import { selectModuleData, updateModuleDataRecord } from '../../redux/moduleDataSlice'
import { fetchSpoofedUserInfo, selectUser } from '../../redux/userSlice'
import { setNotification } from '../../redux/notificationSlice'

// Components
// import ModuleSubPanel from './ModuleSubPanel'
import LayoutSidebar from '../../components/LayoutSidebar'
import dashletDetailViewControl from '../dashlets/dashletDetailViewControl'
import Loading from '../../components/Loading'
import CreatedUpdated from '../../components/elements/CreatedUpdated'
import SubheaderDivider from '../../components/elements/SubheaderDivider'
import SpecialInstruction from '../../components/elements/SpecialInstruction'
import LockNotice from '../../components/elements/LockNotice'
import DisplayField from '../../components/elements/DisplayField'
import ShowMore from '../../components/elements/ShowMore'
import ModuleEditForm from './ModuleEditForm'
// import UserPermissionOption from "../../components/sub_tables/UserPermissionOption"
import CustomerServiceButtons from "../../components/CustomerServiceButtons"
import NoteButton from '../../components/elements/NoteButton'
import { useGetBanners } from "../../utils/detailViewHelper";
import { isDateAfterDays, FormatDateTimeToSame } from '../../helpers/formatting'
import { generateTableKey } from '../../utils/listViewHelper'

const ModuleSubPanel = lazy(() => import('./ModuleSubPanel'));
const UserPermissionOption = lazy(() => import('../../components/sub_tables/UserPermissionOption'));

// Module-scope policy lists (hoisted out of the component so they aren't re-allocated every
// render). These are intentionally separate sets reading TWO different keys — do NOT merge
// them (the membership is pinned by ModuleDetail.test.jsx):
//  - historyModules / downloadCSVModules / bannerModules: action-button + banner enablement.
//  - LOCKABLE_MODULES: records in the record-lock flow, keyed off the URL param
//    `params.module_name` (customer/loan/lead).
//  - CUSTOMER_ACTION_MODULES: records showing the customer-service action fork, keyed off
//    `module.code_key` (customer/loan — NOT lead). Applying LOCKABLE here would wrongly
//    extend Pay Schedule / CustomerServiceButtons / Verifications to lead.
const historyModules = ['payment', 'loan', 'lead', 'customer', 'bankruptcy_monitoring', 'bank_account', 'customer_note', 'loan_dmc', 'customer_reassignment']
const downloadCSVModules = ['payment_return_file', 'payment_batch']
const bannerModules = ['customer']
const LOCKABLE_MODULES = ['customer', 'loan', 'lead']
const CUSTOMER_ACTION_MODULES = ['customer', 'loan']

/**
 * Main Detail page for all of LOX Admin. This page is controlled by the BE module system and renders all the fields,
 * the form and the sub panels dynamically based on that system.
 * NOTE: There is quite a bit of custom logic that is module specific. When adding new custom logic add a block comment
 *  specifying the code as CUSTOM and put the module name that is goes with. EX: MODULE: System Change Log
 * @returns {JSX.Element}
 * @constructor
 */
export default function ModuleDetail() {
  let count = 0
  const [editMode, setEditMode] = useState(false)
  const [displayLockNotice, setDisplayLockNotice] = useState(false)
  const [saving, setSaving] = useState(false)
  const [row, setRow] = useState()
  const [showAll, setShowAll] = useState(false)
  const [module, setModule] = useState()
  const [moduleFields, setModuleFields] = useState()
  const [showMore, setShowMore] = useState(false)
  const [userPermissions, setUserPermissions] = useState()
  const requesting = useRef(false)
  const customerFetchedForKey = useRef(null)
  const requests = useRequest()
  const params = useParams()
  const navi = useNavigate()
  const location = useLocation()
  const modules = useSelector(selectModules)
  const dropDowns = useSelector(selectDropdowns)
  const auth = useSelector(selectAuth)
  const dropdownOptions = useSelector(selectDropdownOptions)
  const modal = useSelector(selectModal)
  const active = useSelector(selectUser)
  const moduleData = useSelector(selectModuleData)
  const dispatch = useDispatch()
  const [redirectedData, setRedirectedData] = useState(false)
  const [forceRecordUnlockPermission, setForceRecordUnlockPermission] = useState(false)
  const [lockNotificationText, setLockNotificationText] = useState()
  const [lockAgentUserId, setLockAgentUserId] = useState()
  const [lockCheck, setLockCheck] = useState(false)
  const [isCloning, setIsCloning] = useState(false)
  const { getBanners, isLoadingBanners, banners, setBanners } = useGetBanners();
  const [csvDownloaded, setCSVDownloaded] = useState(false);
  const [csvDownloading, setCsvDownloading] = useState(false);
  // Detail-record fetch failure: getRecordData sets this on a non-404 error so the
  // page can show a failure banner instead of an indefinite Loading spinner.
  const [recordError, setRecordError] = useState(null);

  //Refresh the row if the id param change
  useEffect(() => {
    setRow(null);
    setRecordError(null);
  }, [params.id]);

  /**
   * Check if the user has correct permissions
   */
  useEffect(() => {
    if (active.status === 'succeeded' && module) {
      const activePermissions = active.spoofed_user.user_permission_options ? active.spoofed_user.user_permission_options : active.data.user_permission_options
      setUserPermissions(activePermissions)
      if (hasUserPermission(activePermissions, `force_record_unlock`)) {
        setForceRecordUnlockPermission(true)
      }
      if (!hasUserPermission(activePermissions, `${module.code_key}_read`)) {
        navi('/403')
      }
    }
  }, [active, module])

  /**
   * Check to ensure the requested module exists and store the data in local state
  */
  useEffect(() => {
    if (modules.data.length > 0) {
      const m = modules.data.find((e) => e.code_key === params.module_name)

      if (m) {
        setModule(m)

        // Sort Fields
        const mf = [...m.module_fields]
        mf.sort((a, b) => a.detail_display_rank - b.detail_display_rank)
        setModuleFields(mf)
      } else {
        navi('/404')
      }
    }
  }, [modules.data, params.module_name])

  /**
   * Determine whether to open the Dashlet sidebar by default
   */
  useEffect(() => {
    if (module && module.module_related_dashlets.length > 0) {
      dispatch(setSidebarIsActive({ sidebarIsActive: true }))
    } else {
      dispatch(setSidebarIsActive({ sidebarIsActive: false }))
    }
  }, [module])

  /**
   * If redirecting back here after submitting a form show the form notification for success
   */
  useEffect(() => {
    if (modules && row && !redirectedData && location.state?.redirectedData) {
      setRedirectedData(location.state.redirectedData)
      window.history.replaceState({}, document.title)
      dispatch(setNotification({ type: 'success', body: redirectedData }))
    }
  }, [modules, row])

  /**
   * Find the show more field if one exists
   */
  useEffect(() => {
    if (moduleFields && dropdownOptions.length > 0) {
      setShowMore(
        moduleFields.find(
          (e) => dropdownOptions[e.module_field_type_dropdown_option_id].code_key
            === MODULE_FIELD_TYPES.SHOWMORE.code_key
        )
      )
    }
  }, [moduleFields, dropdownOptions])

  /**
   * Get record data from api based on module endpoint
   * @returns {Promise<void>}
   */
  const getRecordData = async () => {
    try {
      setRecordError(null)
      requests.setEndpoint(
        `${process.env.REACT_APP_API_DOMAIN}/${module.code_key}/${params.id}`
      )
      const response = await requests.get()
      setRow(response.data.data)
    } catch (error) {
      if (error?.response?.status === 404) {
        navi('/404')
      } else {
        setRecordError(error.response?.data?.message || 'Failed to load this record.')
        dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
      }
    }
  }

  // CUSTOM: customer — only the customer detail view fetches advocacy/collections banners.
  useEffect(() => {
    if (bannerModules.includes(module?.code_key) && row?.id && !banners && !isLoadingBanners) {
      getBanners(module.code_key, row.id)
    } else {
      setBanners(null)
    }
  }, [row, module])

  useEffect(() => {
    if (!auth.fromPageChange) { return }
    if (!module || !moduleData || !auth.accessToken) {
      return;
    }

    // CUSTOM: loan_agreement — reload from the API so the agreement body comes back from S3.
    if (module && module.code_key === 'loan_agreement') {
      //if module is loan agreement just reload the data to get the body content from s3
      getRecordData()
    } else if (module.code_key === 'customer') {
      // CUSTOM: customer. Search rows lack has_active_loan/has_pending_lead/oel_fields, so always
      // fetch. Once per location.key: this effect re-runs on every token refresh.
      if (customerFetchedForKey.current !== location.key) {
        customerFetchedForKey.current = location.key
        getRecordData()
      }
    } else if (location.state && location.state.module_data) {
      // If coming from create form use the data returned from API
      setRow(location.state.module_data)
    } else if (module && moduleData.module_id === module.id) {
      // If coming from the list view use the data from Redux
      setRow(moduleData.data.find((m) => m.id === parseInt(params.id, 10)))
    } else if (!requesting.current && module && moduleData.status === 'idle' && auth.accessToken) {
      getRecordData()
      if (bannerModules.includes(module?.code_key) && row?.id && !isLoadingBanners) {
        getBanners(module.code_key, row.id)
      }
    } else if (moduleData.status === 'succeeded' && moduleData.module_id === module?.id) {
      setRow(moduleData.data[0])
    } else if (location?.state?.reloadPage && auth.accessToken) {
      getRecordData()
    } else {
      // forwarding from subpanel to detail page
      setRow(null)
      getRecordData()
    }
  }, [module, moduleData, auth])

  /**
   * On Click Handler to display the History Modal (Drawer)
   */
  const slideDownHistory = () => {
    if (modal.is_active === false) {
      dispatch(
        // add here
        setModal({ is_active: true, title: `${module.name} History`, content: `${process.env.REACT_APP_API_DOMAIN}/${module.code_key}/${params.id}/${module.code_key}_audit`, type: 'history', parent_modal: `${module.code_key}` })
      )
    } else {
      dispatch(setModal({ title: `${module.name} History`, content: '', type: '', is_active: false }))
    }
  }

  const downloadCSV = async () => {
    try {
      setCsvDownloading(true);
      requests.setEndpoint(
        `${process.env.REACT_APP_API_DOMAIN}/${module.code_key}/${params.id}/download_csv`
      )
      const response = await requests.get();
      if (response.status === 200) {
        setCSVDownloaded(true);
        setCsvDownloading(false)
      }
    } catch (error) {
      if (error?.response?.status === 404) {
        navi('/404')
      } else {
        dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
      }
    }
  }

  // Display data filter created_at Order of IF's important
  const columnDataRender = (data, key) => {
    const fieldType = dropdownOptions[data.module_field_type_dropdown_option_id]?.code_key

    // Filter Columns/Types
    if (DATE_FIELDS.includes(data.column_name)
      || data.column_name === 'id'
      || fieldType === MODULE_FIELD_TYPES.SHOWMORE.code_key) {
      return ''
    }

    if (fieldType === MODULE_FIELD_TYPES.SPECIAL_INSTRUCTIONS.code_key) {
      return <SpecialInstruction name={data.label_name} key={key} content={data.helper_text} />
    }

    // Only Count the fields if they are rendered
    count++

    // If showMore option exists render all BEFORE showMore field
    if (showMore) {
      if (data.detail_display_rank < showMore.detail_display_rank || showAll) {
        // Display Sub header
        if (fieldType === MODULE_FIELD_TYPES.SUBHEADER.code_key) {
          return (
            <SubheaderDivider
              order={count}
              key={key}
              name={data.label_name}
            />
          )
        }
        // Or Render Data
        return (
          <DisplayField
            data={data}
            row={row}
            key={key}
            dropdowns={dropDowns}
            dropdownOptions={dropdownOptions}
          />
        )
      }
    } else {
      // Display Sub header for Non showMore module
      if (fieldType === MODULE_FIELD_TYPES.SUBHEADER.code_key) {
        return (
          <SubheaderDivider
            order={count}
            key={key}
            name={data.label_name}
          />
        )
      }
      // Or Render Data for Non showMore module
      return (
        <DisplayField
          data={data}
          row={row}
          key={key}
          dropdowns={dropDowns}
          dropdownOptions={dropdownOptions}
        />
      )
    }
    return ''
  }

  /**
   * Contains all custom buttons for verifications, customer advocacy, customer support and collections
   * @returns {JSX.Element}
   */
  const renderCustomerActions = (noteButton) => {
    return (
      <>
        {/* @TODO: Need to figure out if the button sets will overlap or not. If they will overlap, move all of the
              buttons into a component and use permissions to determine which ones to show. If not, have different
              components for each button set and use permissions here? */}
        {/* CUSTOM: loan — loan detail shows only a Pay Schedule button; the else branch (customer)
            shows CustomerServiceButtons + Verifications + Pay Schedule. */}
        {module?.code_key == 'loan' ? (
          <button
            type="button"
            className="button has-background-grey has-text-white is-small header-button"
            onClick={() => {
              navi(`/payment_schedule/${row.customer_id}?loan=${row.guid}`)
            }}
          >
            <span className="mx-3">
              Pay Schedule
            </span>
          </button>
        ) : (
          <>
            <CustomerServiceButtons customer={row} module={module?.code_key} disableEdit={displayLockNotice && lockAgentUserId !== active.data.id} submitUnlock={submitUnlock}>
              {noteButton}
            </CustomerServiceButtons><div className="header-button-sub-container">
              <button
                type="button"
                disabled={!row?.has_pending_lead}
                className="button has-background-grey has-text-white is-small mr-1 header-button"
                onClick={() => {
                  navi(`/manual_verification/${params.id}`)
                }}
              >
                <span className="mx-3">
                  Verifications
                  {!row?.has_pending_lead && (
                    <span
                      className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
                      data-tooltip={`Only accessible when there is a pending lead.`}
                    >
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className={`pl-1 has-text-link`}
                      />
                    </span>
                  )}
                </span>
              </button>
              <button
                type="button"
                disabled={!row.has_active_loan}
                className="button has-background-grey has-text-white is-small header-button"
                onClick={() => {
                  navi(`/payment_schedule/${params.id}`)
                }}
              >
                <span className="mx-3">
                  Pay Schedule
                  {!row?.has_active_loan && (
                    <span
                      className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
                      data-tooltip={`Only accessible when a valid loan schedule exists.`}
                    >
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className={`pl-1 has-text-link`}
                      />
                    </span>
                  )}
                </span>
              </button>
            </div>
          </>
        )}
      </>
    )
  }

  /**
   * Called by ModuleEditForm after a successful save. Owns the page-level
   * side effects (record refresh, exit edit, cache refresh, sidebar) while the
   * form itself owns its own RHF state.
   */
  const handleRecordSaved = (updatedRow) => {
    setRow(updatedRow)
    // Keep the list cache in sync — the row-selection effect re-reads it on every auth change.
    dispatch(updateModuleDataRecord({ module_id: module.id, record: updatedRow }))
    setEditMode(false)
    // CUSTOM: module/module_field/dropdown/dropdown_option — editing a metadata module must
    // re-prime its Redux cache so the rest of the app sees the change.
    if (module.code_key === 'module' || module.code_key === 'module_field') {
      dispatch(fetchModules(auth.accessToken))
    } else if (module.code_key === 'dropdown' || module.code_key === 'dropdown_option') {
      dispatch(fetchDropdowns(auth.accessToken))
    }
    if (module && module.module_related_dashlets.length > 0) {
      dispatch(setSidebarIsActive({ sidebarIsActive: true }))
    }
  }

  const handleCancelEdit = () => {
    setEditMode(false)
    if (module && module.module_related_dashlets.length > 0) {
      dispatch(setSidebarIsActive({ sidebarIsActive: true }))
    }
    dispatch(setNotification({ type: '', body: '' }))
  }

  useEffect(() => {
    // CUSTOM: customer/loan/lead — unlock the record on entering edit mode for lockable modules.
    if (editMode && LOCKABLE_MODULES.includes(params.module_name)) {
      submitUnlock(1)
    }
  }, [editMode])

  const submitUnlock = async (lock) => {
    if (lock && displayLockNotice) {
      return null;
    }
    const customerId = module.code_key === 'customer' ? row["id"] : row["customer_id"]
    requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/lock`);
    try {
      if (!lock) {
        setSaving(true)
      }
      let body = { lock: lock }
      const response = await requests.modify(body)
      if (response.status === 200) {
        setDisplayLockNotice(lock ? true : false)

        if (response.data.customer_locked) {
          setLockNotificationText('Locked by ' + response.data.lock_details.lock_agent_name + ', expiring at ' + FormatDateTimeToSame(response.data.lock_details.lock_expiration_date, 'MM/dd/yyyy hh:mm:ss a'))
          setLockAgentUserId(active.data.id)
        } else {
          setLockAgentUserId(null)
          setLockNotificationText(null)
        }
        setSaving(false)
      } else {
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        setSaving(false)
      }
    } catch (error) {
      setSaving(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const cloneModel = async () => {
    if (isCloning) {
      return false;
    }
    setIsCloning(true);
    requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/${module.code_key}/${row.id}/clone`);

    try {
      const response = await requests.post([])
      if (response.status === 200) {
        setIsCloning(false)
        setRow(null)
        navi(`/${module.code_key}/${response.data.data.id}`)
      } else {
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        setIsCloning(false)
      }
    } catch (error) {
      setIsCloning(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const showLockNotice = () => {
    if (LOCKABLE_MODULES.includes(params.module_name) &&
      (params.id == row?.id || params.id == row?.customer_id)) {
      const showUnlockButton = forceRecordUnlockPermission || lockAgentUserId === active.data.id;
      return (
        <LockNotice
          content={lockNotificationText}
          showUnlockButton={showUnlockButton}
          submitUnlock={submitUnlock}
          saving={saving}
          setLockCheck={setLockCheck}
          customerId={params.module_name === 'customer' ? row.id : row.customer_id}
          setLockNotificationText={setLockNotificationText}
          setLockAgentUserId={setLockAgentUserId}
          displayLockNotice={displayLockNotice}
          lockCheck={lockCheck}
          setDisplayLockNotice={setDisplayLockNotice}
          requests={requests}
        />
      );
    }
    return null
  }
  const generateActionButtons = () => {
    let listButtons = {
      history: {
        name: 'history',
        options: {
          className: `button has-background-grey has-text-white is-small header-button`,
          style: { width: '5.5rem' },
          onClick: slideDownHistory,
          disabled: displayLockNotice && lockAgentUserId !== active.data.id,
          label: (<><FontAwesomeIcon icon={faHistory} className="pr-1" /> History </>),
        },
        isEnable: userPermissions && hasUserPermission(userPermissions, `${module.code_key}_update`) && historyModules.includes(module.code_key),
      },
      // CUSTOM: loan_agreement — clone is only offered for an active loan_agreement.
      clone: {
        name: 'clone',
        options: {
          className: `button has-background-grey has-text-white is-small mr-1 header-button`,
          style: { width: '3rem' },
          onClick: () => cloneModel(),
          disabled: displayLockNotice && lockAgentUserId !== active.data.id,
          label: (isCloning ? (<>Cloning <Loading noWrapper size="is-size-5" /></>) : 'Clone'),
        },
        isEnable: userPermissions && hasUserPermission(userPermissions, `${module.code_key}_update`) && module.code_key === 'loan_agreement' && row.is_active === 1,
      },
      // CUSTOM: loan_agreement — an active loan_agreement is cloned, not edited (see clone above).
      edit: {
        name: 'edit',
        options: {
          className: `button has-background-grey has-text-white is-small mr-1 header-button`,
          style: { width: '2.5rem' },
          onClick: () => {
            dispatch(setSidebarIsActive({ sidebarIsActive: false }))
            setEditMode(true)
          },
          disabled: displayLockNotice && lockAgentUserId !== active.data.id,
          label: 'Edit',
          testid: 'module-edit-button',
        },
        isEnable: userPermissions && hasUserPermission(userPermissions, `${module.code_key}_update`) && !(module.code_key === 'loan_agreement' && row.is_active === 1),
      },
      delete: {
        name: "delete",
        options: {
          className: "button has-background-danger has-text-white is-small mr-1 header-button",
          style: { width: '3.5rem' }, //only use if there is only 1 button on column
          onClick: () => {
            dispatch(setModal({ is_active: true, type: 'delete_record', options: { baseName: module.code_key, id: row.id, navTo: `/${module.code_key}` } }));
          },
          disabled: false,
          label: "Delete"
        },
        isEnable: userPermissions && (hasUserPermission(userPermissions, `${module.code_key}_delete`) || (row?.uploaded_by_user_id == active.data.id && isDateAfterDays(row?.created_at, 30))) && module.can_delete_record && !(dropdownOptions.find((x) => x?.id === row?.document_type_dropdown_option_id)?.code_key == 'document_type_loan_agreement' && !hasUserPermission(userPermissions, 'is_dev')),
      },
      downloadCSV: {
        name: "downloadCSV",
        options: {
          className: "button has-background-warning has-text-white is-small mr-1 header-button",
          style: { width: '4rem' }, //only use if there is only 1 button on column
          onClick: () => { if (csvDownloaded) { navi('/report/') } else { downloadCSV() } },
          disabled: csvDownloading,
          label: (csvDownloaded ? "Go to Reports" : "Generate Report")
        },
        isEnable: userPermissions && hasUserPermission(userPermissions, `${module.code_key}_download`) && downloadCSVModules.includes(module.code_key),
      },
      // CUSTOM: user — the Spoof User button only appears on the user module for admins who can spoof.
      spoofed_user: {
        name: "spoof_user",
        options: {
          className: "button has-background-grey has-text-white is-small header-button",
          style: {}, //only use if there is only 1 button on column
          onClick: () => {
            dispatch(fetchSpoofedUserInfo({ accessToken: auth.accessToken, spoofed_user_id: params.id }))
            navi('/home')
          },
          disabled: false,
          label: (
            <>
              <FontAwesomeIcon icon={faUserSecret} className={'is-size-6 pr-1'} />
              Spoof User
              <span
                className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary'}
                data-tooltip={'Some actions cannot be performed by the impersonator for security/fraud reasons.'}
              >
                <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
              </span>
            </>
          )
        },
        isEnable: (module.code_key === 'user' && hasUserPermission(active.data.user_permission_options, 'can_spoof_admin')),
      },
    }

    //filtering buttons that is enable
    listButtons = Object.keys(listButtons).filter((key) => {
      return listButtons[key].isEnable
    }).reduce((result, key) => {
      result[key] = listButtons[key];
      return result;
    }, {});

    //converting to index object
    const indexedList = Object.keys(listButtons).reduce((result, key, idx) => {
      result[idx] = listButtons[key];
      return result;
    }, {});

    const numOfListGroup = Math.ceil(Object.keys(indexedList).length / 2);

    // feed the group of 2 button the array
    let listGroups = [];
    let index = 0;
    for (let i = 0; i < numOfListGroup; i++) {
      const button1 = indexedList[index] ?? null;
      const button2 = indexedList[index + 1] ?? null;

      let group = [];
      if (button1) {
        group.push(button1)
      }

      if (button2) {
        group.push(button2)
      }

      listGroups.push(group)
      index += 2;
    }

    return (
      <>
        {CUSTOMER_ACTION_MODULES.includes(module.code_key) ? renderCustomerActions(<NoteButton />) : null}
        {listGroups.map((group) => {
          return generateGroupButton(group)
        })}
      </>

    )
  }

  const generateGroupButton = (group) => {
    let fixedStyle = false
    if (group.length === 2 && group[0].name === 'edit' && group[1].name === 'delete') {
      fixedStyle = { width: '3.5rem' }
    }

    if (group.length === 2 && group[0].name === 'history' && group[1].name === 'edit') {
      fixedStyle = { width: '5.5rem' }
    }

    return (
      <div
        className="header-button-sub-container"
        key={generateTableKey('', 'group-button-item')}
      >
        {group[0] && generateButton(group[0].options, group.length === 1, fixedStyle)}
        {group[1] && generateButton(group[1].options, group.length === 1, fixedStyle)}
      </div>
    )
  }

  const generateButton = (options, isSingle, fixedStyle) => {
    // eslint-disable-next-line no-nested-ternary
    let style = fixedStyle ? fixedStyle : ((isSingle && options.style) ? options.style : {});
    if (isCloning) {
      style = { width: '5.5rem' };
    }
    return (
      <button
        type="button"
        className={options.className ?? ''}
        style={style}
        onClick={options.onClick}
        disabled={options.disabled}
        data-testid={options.testid}
        key={generateTableKey('', options.name + '-button-item')}
      >
        <span className="mx-2">{options.label}</span>
      </button>
    )
  }

  if (modules && row) {
    const title = row['label'] != null
      ? `${row['label']} Details`
      : `${module.name} Details`

    const contentTitle = (
      <>
        {title}
        {module.helper_text && (
          <span
            className="ml-1 lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary"
            data-tooltip={module.helper_text}
          >
            <FontAwesomeIcon icon={faQuestionCircle} className="is-size-4 has-text-link" />
          </span>
        )}
      </>
    )

    return (
      <LayoutSidebar
        key={module.id + row.id}
        id={row.id}
        portfolioLabel={row?.portfolio_label ?? null}
        isLoadingBanners={isLoadingBanners}
        contentTitle={contentTitle}
        banners={banners}
        actionButtons={!editMode ? generateActionButtons() : ''}
        content={(
          <>
            {Object.keys(row)?.length && showLockNotice()}
            {editMode ? (
              <ModuleEditForm
                // Keyed per record so RHF state never carries values from a
                // previously-edited record into this one (LOX stale-data fix).
                key={`${module.code_key}-${params.id}`}
                module={module}
                moduleFields={moduleFields}
                row={row}
                dropDowns={dropDowns}
                dropdownOptions={dropdownOptions}
                active={active}
                onSaved={handleRecordSaved}
                onCancel={handleCancelEdit}
              />
            ) : (

              <div className="lox-detail columns is-multiline pt-3">
                {moduleFields.map((m) => columnDataRender(m, m.id))}
                <ShowMore
                  onClick={() => {
                    setShowAll(!showAll)
                  }}
                  isActive={showAll}
                  isVisible={showMore}
                />
              </div>
            )}

            <Suspense fallback={<div />}>
              <div className={`lox-sub-panel-group columns is-multiline pt-3 pr-3  ${editMode && 'is-hidden'}`}>
                {module.module_related_modules.map((m) => {
                  const subModule = modules.data.find(
                    (e) => e.code_key === m.subpanel_module_code_key
                  )
                  if (subModule && hasUserPermission(userPermissions, `${subModule.code_key}_read`)) {
                    if (subModule.code_key === 'user_permission_option') {
                      return (
                        <UserPermissionOption
                          id={row.id}
                          module={module}
                          subModule={subModule}
                          is_servicer_role={row?.is_servicer_role}
                          key={m.subpanel_module_code_key}
                          hidden={editMode}
                          isPagination={m.is_pagination}
                        />
                      )
                    } else {
                      return (
                        <ModuleSubPanel
                          id={row.id}
                          name={row?.label ? row?.label : row?.name}
                          codeKey={row?.code_key}
                          module={module}
                          subModule={subModule}
                          key={m.subpanel_module_code_key}
                          hidden={editMode}
                          isPagination={m.is_pagination}
                          isOrderable={m.is_orderable}
                          isInlineEditable={m.is_inline_editable}
                          isMappingTable={m.is_mapping_table}
                          parentModuleId={params.id}
                          parentModuleData={row}
                        />
                      )
                    }
                  }
                })}
              </div>
            </Suspense>
            <div className="divider my-3" />
            <CreatedUpdated
              createdAt={row.created_at}
              createdBy={row.created_by}
              updatedAt={row.updated_at}
              updatedBy={row.updated_by}
            />
          </>
        )}
        dashlets={dashletDetailViewControl(module, row, dropdownOptions, active)}
      />
    )
  }
  if (recordError) {
    return (
      <div className="notification is-danger is-light m-5" data-testid="module-detail-load-failed">
        {recordError}
      </div>
    )
  }
  return <Loading />
}
